import { createSelector } from 'reselect';

const selectChallengesState = (state) => state.challengesDetails || {};
const selectLoyaltyProfile = (state) => state.rewardsDetails?.details?.data || {};

export const selectChallenges = createSelector(selectChallengesState, (s) => s.challenges || []);
export const selectShowLoader = createSelector(selectChallengesState, (s) => s.showLoader);
export const selectTier = createSelector(selectLoyaltyProfile, (p) => {
    const { tier } = p;
    if (!tier) return 'Member';
    if (typeof tier === 'string') return tier;
    // tier is an object with { code, name } — normalize to capitalized form
    const code = (tier.code || '').toLowerCase();
    const tierMap = {
        member: 'Member',
        insider: 'Insider',
        elite: 'Elite',
        vip: 'VIP',
        base: 'Member',
        credit: 'Member',
    };
    return tierMap[code] || 'Member';
});
// export const selectOfferSelection = createSelector(selectLoyaltyProfile, (p) => p.offerSelection || {});
export const selectOfferSelection = createSelector(
    selectChallengesState,
    (s) => s.offerSelection || []
);

export const selectIsEligibleTier = createSelector(selectTier, (tier) => tier !== 'Member');

export const selectTierLimit = createSelector(
    selectOfferSelection,
    (offerSelection) => offerSelection.limit || 0
);

export const selectInProgressChallenges = createSelector(selectChallenges, (challenges) =>
    challenges.filter((o) => o.status === 'InProgress')
);

export const selectNotStartedChallenges = createSelector(selectChallenges, (challenges) =>
    challenges.filter((o) => o.status === 'NotStarted')
);

export const selectCompletedChallenges = createSelector(selectChallenges, (challenges) =>
    challenges.filter((o) => o.status === 'Completed')
);

export const selectExpiredChallenges = createSelector(selectChallenges, (challenges) =>
    challenges.filter((o) => o.status === 'Expired')
);

export const selectIsLimitReached = createSelector(
    selectOfferSelection,
    ({ limit = 0, active = 0, completed = 0 }) => limit > 0 && active + completed >= limit
);

export const selectTabCounts = createSelector(
    [
        selectInProgressChallenges,
        selectNotStartedChallenges,
        selectCompletedChallenges,
        selectExpiredChallenges,
        selectIsLimitReached,
    ],
    (inProgress, notStarted, completed, expired, isLimitReached) => ({
        inProgress: inProgress.length,
        notStarted: isLimitReached ? 0 : notStarted.length,
        completed: completed.length,
        expired: expired.length,
    })
);

// Configurable urgency threshold (days before end date to trigger "ending soon")
const URGENCY_DAYS_THRESHOLD = 14;

export const makeSelectUrgencyMessage = () =>
    createSelector(
        (_, challenge) => challenge,
        (challenge) => {
            const { threshold, progressCompleted, endDate } = challenge;
            const remaining = threshold - progressCompleted;

            if (remaining <= 0) return null;

            const daysUntilExpiry = Math.ceil(
                (new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24)
            );

            // Don't show urgency message if endDate is in the past
            // if (daysUntilExpiry < 0) return null;
            // AC28: Almost There — exactly 1 away AND end date is more than 14 days away
            if (
                remaining === 1 &&
                progressCompleted > 0 &&
                daysUntilExpiry > URGENCY_DAYS_THRESHOLD
            ) {
                return {
                    type: 'almostThere',
                    text: [
                        { text: 'ALMOST THERE!', bold: true },
                        { text: ' — Only 1 purchase away from completing this challenge!' },
                    ],
                };
            }

            // AC29: Ending Soon — approaching end date AND still has remaining purchases
            if (daysUntilExpiry <= URGENCY_DAYS_THRESHOLD && remaining > 0) {
                return {
                    type: 'hurry',
                    text: [
                        { text: 'HURRY!', bold: true },
                        { text: ` — Challenge ending soon! You're only ` },
                        { text: `${remaining}`, bold: true },
                        { text: ` purchases away from completion.` },
                    ],
                };
            }

            return null;
        }
    );
